import { FC } from 'react'

import {
  Avatar,
  Box,
  type ColorUnion,
  SearchField,
  Tag,
  Text,
  Title,
} from '@factorialco/deprecated-design-system'
import { PeopleChartMembersDetailFragment } from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type Props = {
  members: PeopleChartMembersDetailFragment | undefined
  color: ColorUnion
  onEmployeeSelection: (employeeId: number) => void
}

const getFilteredMembers = (search: string, members: PeopleChartMembersDetailFragment) => {
  const filtered = members.activeMemberships.nodes.filter((member) => {
    const employeeName = member.employee?.fullName.toLowerCase() || ''
    const filterByName = employeeName.includes(search.toLowerCase())
    return filterByName
  })
  return orderBy(
    filtered,
    [(member) => member.lead, (member) => member.employee.fullName],
    ['desc', 'asc']
  )
}

const MembersDetail: FC<Props> = ({ members, color, onEmployeeSelection }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const filteredMembers = useUnsafeMemo(() => {
    if (members) {
      return getFilteredMembers(debouncedSearch, members)
    } else {
      return []
    }
  }, [debouncedSearch, members])

  const jobDisplay = (
    jobTitle: string,
    jobRole: string | null | undefined,
    jobLevel: string | null | undefined,
    isDefault: boolean
  ) => {
    if (jobTitle) {
      return jobTitle
    }

    if (isDefault) {
      return jobRole
    }

    return [jobRole, jobLevel].filter(Boolean).join(',')
  }

  return (
    <Box gap="s12" maxWidth="full">
      <Title title={i18n.t('people_chart.sidebar.people')} />
      <Box flexDirection="row" gap="s8" style={{ maxWidth: '540px', width: '540px' }}>
        <SearchField
          onSearch={onSearch}
          placeholder={i18n.t('people_chart.sidebar.name')}
          search={search}
          variant="regular"
          loading={search !== debouncedSearch}
        />
      </Box>

      <Box gap="s16" width="full">
        {filteredMembers.map((member) => (
          <Box
            key={`to-employee-sidepanel-${member.employee.id}`}
            onClick={() => onEmployeeSelection(member.employee.id)}
          >
            <Box
              borderRadius={{ all: 'abs016' }}
              alignItems="center"
              background="grey200"
              key={member.id}
              flexDirection="row"
              gap="s12"
              padding="s16"
              style={{ maxWidth: '540px', width: '540px' }}
              cursor="pointer"
            >
              <Avatar
                label={member.employee?.fullName || ''}
                url={buildUrl(member.employee?.access?.avatar) || ''}
              />
              <Box alignItems="flexStart" gap="s4">
                <Box
                  style={{ maxWidth: '440px', width: '440px' }}
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  gap="s12"
                >
                  <Text.Normal weight="bold">{member.employee?.fullName}</Text.Normal>
                  {member.lead && <Tag type={color}>{i18n.t('people_chart.sidebar.lead')}</Tag>}
                </Box>
                <Text.Normal>
                  {jobDisplay(
                    member.employee?.referenceContract?.jobTitle || '',
                    member.employee?.referenceContract?.jobCatalogLevel?.roleName,
                    member.employee?.referenceContract?.jobCatalogLevel?.name,
                    member.employee?.referenceContract?.jobCatalogLevel?.isDefault || false
                  )}
                </Text.Normal>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default MembersDetail
